import { datadogRum } from "@datadog/browser-rum";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  LoaderFunction,
  Navigate,
  Outlet,
  redirect,
  useLoaderData,
  useRouteError,
} from "react-router-dom";

import {
  getReferralCodeFromUrl,
  openLinkToMobileApp,
  setIdleTimeInTimestamp,
  showViewAppFooter,
} from "@/services/DeepLinkService";
import { get } from "api";
import { AdonisChatProvider } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AppIconListener from "modules/appIcon/AppIconListener/AppIconListener";
import {
  PushNotificationProvider,
  pushNotificationService,
} from "modules/pushNotifications";
import TopNav from "molecules/navbars/TopNav";
import { Footer } from "molecules/navigation/footer/Footer";
import { ViewAppFooter } from "molecules/view-app-footer/ViewAppFooter";
import { queryClient } from "pages/Root";
import RootError from "pages/RootError";
import { identifyFlagUser } from "services/Flags";
import { currencyService } from "toolbox/CurrencyService";
import { changeDayjsLocale } from "utils/dayjsUtils";
import { setPromotionModalSeen } from "utils/functions";
import { isNativePlatform } from "utils/platform.helpers";

import LoadingLayout from "./LoadingLayout";

// growthbook flags refresh the router while we are getting
// ready for v3. This load runs inside the router, which in
// turn refrreshes the flags -> causing a loop.
const previousLoadUserRef = {
  id: "",
};
const load = async () => {
  const user = await queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 0,
  });

  await queryClient.fetchQuery({
    queryKey: ["/user/preferences"],
    queryFn: () => get("/user/preferences"),
    staleTime: 0,
  });

  queryClient.setQueryDefaults(["/user"], {
    staleTime:
      // No staleTime if pre-registered, otherwise 2 minutes
      !user.completedProfile && user.hasBeenPreRegistered ? 0 : 1000 * 60 * 2,
  });

  datadogRum.setUser({
    id: user.id,
    name: user.phoneNumber,
    email: user.email ?? undefined,
    platformId: user.platform?.id ?? undefined,
  });

  if (previousLoadUserRef.id !== user.id) {
    identifyFlagUser(
      {
        id: user.id,
        name: user.fullName ?? undefined,
        email: user.email ?? undefined,
      },
      user.platform && {
        id: user.platform.id,
        domain: user.platformDomain?.domain ?? undefined,
        created: dayjs(user.platform.created).toDate(),
      }
    );
  }

  return user;
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const appLayoutLoader: LoaderFunction = async ({ request }) => {
  const user = await load();
  const platform = await queryClient.fetchQuery(["/platform/current"], () =>
    get("/platform/current").catch(() => null)
  );
  changeDayjsLocale(platform?.stripeRegion);
  currencyService.setCurrency(platform?.stripeRegion);

  const authClaims = await queryClient.ensureQueryData({
    queryKey: ["/auth/claims"],
    queryFn: () => get("/auth/claims"),
  });

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  if (!isAdminImpersonation) {
    // Check if platform is deactivated and redirect to deactivated page
    if (platform && !platform.active) {
      return redirect("/deactivated");
    }

    // Enforce V3 onboarding flow for authenticated users when enabled
    // Allow anonymous (guest) users to access Home without being redirected into onboarding
    if (!user.isGuest) {
      // DOB is optional: if email is missing AND dob is missing, go to DOB first
      if (!user.firstName || !user.lastName) {
        return redirect("/signin/name");
      }
      if (!user.email) {
        return redirect("/signin/email");
      }
      if (!user.gender) {
        return redirect("/signin/gender");
      }
      if (!user.signUpSource) {
        return redirect("/signin/source");
      }
    }
  }

  return user;
};

/**
 * Layout route for authenticated users (post-login)
 */
const AppLayout = () => {
  const user = useLoaderData() as LoaderData;
  const referralCode = getReferralCodeFromUrl(location.href);

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const mobileAppDiscovery = platform?.platformSettings?.mobileAppDiscovery;
  const [displayViewAppFooter, setDisplayViewAppFooter] = useState(
    mobileAppDiscovery && showViewAppFooter(user?.id)
  );

  useEffect(() => {
    if (displayViewAppFooter) {
      setDisplayViewAppFooter(showViewAppFooter(user?.id));
    }
  }, [displayViewAppFooter, user?.id]);

  return (
    <PushNotificationProvider
      isNativePlatform={isNativePlatform()}
      pushNotificationService={pushNotificationService}
    >
      <AdonisChatProvider platformSettings={platform?.platformSettings}>
        <AppIconListener />
        {displayViewAppFooter && (
          <ViewAppFooter
            platformName={platform?.name || ""}
            open={displayViewAppFooter}
            onClose={() => {
              setDisplayViewAppFooter(false);
              setIdleTimeInTimestamp(user?.id);
            }}
            onOpenApp={() => openLinkToMobileApp(platform?.id, referralCode)}
          />
        )}
        <LoadingLayout />
        <div className="flex flex-auto">
          <div className="w-full">
            <Outlet />
          </div>
        </div>
        <div className="sticky bottom-0 left-0 right-0 z-40 flex justify-center">
          <div className="w-full sm:w-[390px]">
            <Footer />
          </div>
        </div>
      </AdonisChatProvider>
    </PushNotificationProvider>
  );
};

export const AppLayoutError = () => {
  const error = useRouteError();

  const { data: logo } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
    select: (data) => data.logoImageUrl,
  });

  if (isAxiosError(error) && error.response?.status === 401) {
    setPromotionModalSeen(false);
    return <Navigate to="/signin" />;
  }

  return (
    <>
      <TopNav>{logo && <img src={logo} className="h-8" />}</TopNav>
      <RootError />
      <div className="sticky bottom-0 left-0 right-0 z-40 flex justify-center">
        <div className="w-full sm:w-[390px]">
          <Footer />
        </div>
      </div>
    </>
  );
};

export default AppLayout;
